import type { IPlatformTask } from "./checklist";

export type TOnboardingPlan = "trial" | "starter" | "pro" | "team";
export type TOnboardingRole = "admin" | "creator" | "creator-lite" | "user";

type TTaskEntry = string | { key: string; credits?: number };

const STORYDOC_PRO = [
  "webinar-library",
  "design-workshop",
  "follow-linkedin",
  "youtube-videos",
];
const SPREAD_THE_WORD = ["review-trustpilot", "review-g2", "share-social"];
const COMMUNITY = [...STORYDOC_PRO, ...SPREAD_THE_WORD];

const BASE_CONTENT = [
  "view-first-analytics",
  "create-personalized-version",
  "organize-with-folders",
  "set-up-brand",
  "define-ai-storybrain",
  "set-up-media-library",
];

const QUICK_WINS = [
  "finish-platform-tour",
  "complete-profile",
  "share-first-storydoc",
];

const TRIAL_CREATOR: TTaskEntry[] = [
  ...QUICK_WINS,
  ...BASE_CONTENT,
  ...COMMUNITY,
];

const TEAM_VERSIONS_ONLY: TTaskEntry[] = [
  "finish-platform-tour",
  "complete-profile",
  { key: "share-first-storydoc", credits: 0 },
  "view-first-analytics",
  "create-personalized-version",
];

/**
 * Hub tasks per plan and role, as defined in the Storydoc Onboarding Matrix.
 * Order follows the checklist; an entry's credits override the task's own.
 */
export const ONBOARDING_TASK_SETS: Record<
  TOnboardingPlan,
  Partial<Record<TOnboardingRole, TTaskEntry[]>>
> = {
  trial: {
    admin: [
      ...QUICK_WINS,
      { key: "invite-colleague", credits: 250 },
      ...BASE_CONTENT,
      "team-plan-walkthrough",
      ...COMMUNITY,
    ],
    creator: TRIAL_CREATOR,
  },
  starter: {
    admin: [
      ...QUICK_WINS,
      { key: "invite-colleague", credits: 500 },
      "view-first-analytics",
      "create-personalized-version",
      "set-up-brand",
      "define-ai-storybrain",
      "set-up-media-library",
      "team-plan-walkthrough",
      ...COMMUNITY,
    ],
    creator: TRIAL_CREATOR,
  },
  pro: {
    admin: [
      ...QUICK_WINS,
      { key: "invite-colleague", credits: 500 },
      "view-first-analytics",
      "add-version-variable",
      "create-personalized-version",
      "organize-with-folders",
      "set-up-brand",
      "define-ai-storybrain",
      "set-up-media-library",
      "team-plan-walkthrough",
      "connect-integration",
      "first-api-call",
      "create-subdomain",
      ...COMMUNITY,
    ],
    creator: [
      ...QUICK_WINS,
      "view-first-analytics",
      "add-version-variable",
      "create-personalized-version",
      "organize-with-folders",
      "set-up-brand",
      "define-ai-storybrain",
      "set-up-media-library",
      ...COMMUNITY,
    ],
  },
  team: {
    admin: [
      ...QUICK_WINS,
      { key: "invite-colleague", credits: 0 },
      "view-first-analytics",
      "add-version-variable",
      "create-personalized-version",
      "organize-with-folders",
      "set-up-brand",
      "define-ai-storybrain",
      "create-team-variable",
      "set-up-profile-variables",
      "set-up-media-library",
      "connect-integration",
      "first-api-call",
      "create-subdomain",
      "custom-domain",
      ...COMMUNITY,
    ],
    creator: [
      ...QUICK_WINS,
      "view-first-analytics",
      "add-version-variable",
      "create-personalized-version",
      "organize-with-folders",
      "set-up-brand",
      "define-ai-storybrain",
      "create-team-variable",
      "set-up-profile-variables",
      "set-up-media-library",
      ...COMMUNITY,
    ],
    "creator-lite": [...TEAM_VERSIONS_ONLY, ...COMMUNITY],
    user: [
      ...TEAM_VERSIONS_ONLY,
      "webinar-library",
      "follow-linkedin",
      "youtube-videos",
      ...SPREAD_THE_WORD,
    ],
  },
};

/**
 * Creator Lite and User exist on Team only; on another plan they get the Team set for
 * their role, which only contains tasks every plan supports.
 */
export const resolveTaskSet = (
  plan: TOnboardingPlan,
  role: TOnboardingRole,
): TTaskEntry[] =>
  ONBOARDING_TASK_SETS[plan][role] ??
  ONBOARDING_TASK_SETS.team[role] ??
  ONBOARDING_TASK_SETS[plan].creator ??
  [];

export const buildTasks = (
  entries: TTaskEntry[],
  catalog: IPlatformTask[],
): IPlatformTask[] => {
  const byKey = new Map(catalog.map((task) => [task.key, task]));
  return entries.flatMap((entry) => {
    const key = typeof entry === "string" ? entry : entry.key;
    const task = byKey.get(key);
    if (!task) return [];
    if (typeof entry === "string") return [task];
    return [
      {
        ...task,
        ...(entry.credits !== undefined
          ? { credits: entry.credits || undefined }
          : {}),
      },
    ];
  });
};
